// Remembers the method of the last successful sign-in so /login can put it first (JA-652)
export const LAST_LOGIN_PROVIDER_KEY = "joylo_last_login_provider";

export type LoginProvider = "email" | "google" | "github" | "microsoft" | "apple" | "facebook";

const KNOWN_PROVIDERS: readonly LoginProvider[] = ["email", "google", "github", "microsoft", "apple", "facebook"];

const isLoginProvider = (value: unknown): value is LoginProvider =>
  typeof value === "string" && (KNOWN_PROVIDERS as readonly string[]).includes(value);

export const setLastLoginProvider = (provider: string | null | undefined): void => {
  const normalized = provider?.toLowerCase();
  if (!isLoginProvider(normalized)) return;
  try {
    localStorage.setItem(LAST_LOGIN_PROVIDER_KEY, normalized);
  } catch {
    // Storage can be blocked (private mode); remembering the provider is best-effort
  }
};

export const getLastLoginProvider = (): LoginProvider | null => {
  try {
    const value = localStorage.getItem(LAST_LOGIN_PROVIDER_KEY);
    return isLoginProvider(value) ? value : null;
  } catch {
    return null;
  }
};

// Moves the remembered provider to the front; anything not currently offered keeps the default order
export const orderByLastUsed = <T extends { id: string }>(
  items: readonly T[],
  lastUsed: string | null,
): { items: T[]; lastUsedId: string | null } => {
  const index = lastUsed ? items.findIndex((item) => item.id === lastUsed) : -1;
  if (index === -1) return { items: [...items], lastUsedId: null };
  return { items: [items[index], ...items.filter((_, i) => i !== index)], lastUsedId: items[index].id };
};
